import { Injectable, inject } from '@angular/core';
import { IPromise } from 'angular';

import { createPrimaryCardholderSchema } from '@acme/b2b/membership/cardholder';
import { Notice } from '@b2b/common/factories/notice';
import { VCardModalResult } from '@b2b/common/views/modals/vcard';
import { Address } from '@b2b/types/core';
import { extractEmbeddedAddressFromCardholder, MembershipCardholder } from '@b2b/types/membership';

import { AddressService } from '../../address/address.service';
import { ModalService } from '../../modal/modal.service';

/**
 * MembershipCardholderService (Angular 18)
 *
 * Ported from apps/b2b-ui/common/services/membership/cardholder/membership-cardholder.service.ts
 * (already-TS, legacy AngularJS name 'MembershipCardholderService'). Validates
 * cardholders and opens the vCard modal for create/edit flows.
 *
 * Dependency mapping: ModalService and AddressService are both Angular 18 ✅,
 * so they are injected directly (inject(), no constructor injection per the
 * hybrid-bundle DI guidance). Registered back into AngularJS under
 * 'MembershipCardholderService' via downgradeInjectable in
 * app/upgrade/ng-downgraded-components.ts. Wave 3 #28.
 */
@Injectable({ providedIn: 'root' })
export class MembershipCardholderService {
  private modalService = inject(ModalService);
  private addressService = inject(AddressService);

  validateForPrimary(cardholder: MembershipCardholder, metadata: { isAutorenewMembership: boolean }) {
    const schema = createPrimaryCardholderSchema(metadata);
    const result = schema.safeParse(cardholder);

    return result.success;
  }

  /**
   * Swapping primary/secondary cards is not allowed for AR memberships with active subscriptions.
   *
   * @link https://acmeticketing.atlassian.net/browse/APP-2269
   */
  checkAllowedToSwapPrimaryCardholder({ isAutorenewMembership }: { isAutorenewMembership: boolean }) {
    if (isAutorenewMembership) {
      return {
        ok: false,
        errors: [
          `Auto Renew subscriptions are owned by the primary cardholder on a membership and cannot be transferred to another customer.`,
        ],
      };
    }

    return { ok: true, errors: [] };
  }

  showCardholderModal(props: {
    mode: 'create' | 'edit' | 'edit.dedup';
    cardholder: MembershipCardholder | null;
    isAutorenewMembership: boolean;
    membershipAddresses?: { id: number; addresses: Address[] };
  }) {
    const edit = props.mode.startsWith('edit');
    const originalCardholderAddress = props.cardholder
      ? extractEmbeddedAddressFromCardholder(props.cardholder)
      : undefined;

    if (props.membershipAddresses) {
      /**
       * // HACK: We insert the original's cardholder address at the top of the list to make sure
       * it makes it to the options in cardholder modal after dedup.
       */
      const membershipAddresses = originalCardholderAddress
        ? [originalCardholderAddress, ...props.membershipAddresses.addresses]
        : props.membershipAddresses.addresses;

      this.addressService.loadAddressesForMembership({
        id: props.membershipAddresses.id,
        addresses: this.addressService.uniqueByName(membershipAddresses),
      });
    }

    return this.modalService.open<VCardModalResult>({
      size: 'lg',
      backdrop: 'static',
      controller: 'VcardModalController',
      templateUrl: 'common/views/modals/vcard/vcard.tpl.html',
      resolve: {
        mode: () => props.mode,
        isSearch: () => (edit ? false : true),
        modalTitle: () => (edit ? 'Edit Cardholder' : 'Add Cardholder'),
        saveText: () => (edit ? 'Save' : 'Add'),
        CardData: () => {
          if (edit) {
            /**
             * Cardholder modal expects the model to have a nested `address` object
             */
            if (props.cardholder && props.cardholder.address == null) {
              props.cardholder.address = extractEmbeddedAddressFromCardholder(props.cardholder);
            }

            return props.cardholder;
          }

          return { cardType: 'secondary', address: {} };
        },
        AddressId: () => props.membershipAddresses?.id,
        CustomerDuplicatesData: () => null,
        ModalData: () => {
          return {
            isAutorenewMembership: props.isAutorenewMembership,
            membershipErrors: {
              missingPrimaryCardEmailForAutorenew: {
                message: 'Required fields are missing for the primary cardholder.',
              },
            },
            onMakePrimaryClick: ({ event, notice }: { event: PointerEvent; notice: Notice }) => {
              const result = this.checkAllowedToSwapPrimaryCardholder(props);

              if (!result.ok) {
                event.preventDefault();

                const messages = result.errors.map((message) => ({ message }));
                notice.showErrorMessages(messages, { duration: 10_000 });
                return;
              }
            },
          };
        },
      },
    });
  }

  showPrimaryCardholderChangedMessage(notice: Notice): IPromise<void> {
    return notice.showSuccessMessages([{ message: 'Primary cardholder change successfully saved.' }], {
      duration: 3000,
    });
  }
}
